/* ══════════════════════════════════════════════════════════════════
   BUSTAH — the shop runtime

   Everything a customer does happens on the page they are already on.
   A product opens in a panel, the bag is a panel, the saved list is a
   panel. The only navigation away from the page is the checkout button.

   Two things make that possible here that were not possible when this
   design was a static file on another host:

   1. The AJAX cart. /cart/add.js and friends are same-origin now, so
      the bag is Shopify's real cart — real prices in the visitor's own
      market, real discounts, and the checkout button hands over a cart
      that already exists rather than rebuilding one from a permalink.

   2. Alternate templates. Asking for ?view=popup on a product URL
      renders templates/product.popup.liquid, which declares
      {% layout none %} and so returns exactly the fragment wanted and
      nothing else. The popup, the bag and the saved list are therefore
      built by Liquid from live store data, by the same snippets that
      build the real pages. There is one product card in this theme, not
      two, and a popup cannot show a price, a photograph or a stock
      state that has gone stale — it is the product, fetched when it is
      opened.

   Which is also why there is no money formatting in this file. Every
   figure a customer sees was rendered by Liquid.

   Progressive enhancement, deliberately: every trigger in the markup is
   a real <a href> to a real page. This script intercepts the click. A
   crawler — or a visitor whose JavaScript failed — follows the link and
   gets the product page. Nobody is stranded, and Google sees a properly
   linked catalogue rather than a wall of buttons it cannot follow.
   ══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var CFG = window.__bustah || {};
  var R = CFG.routes || {};
  var T = CFG.strings || {};
  var K_SAVE = 'bustah.saved';
  var busy = false;

  function t(key, fallback) { return T[key] || fallback; }

  /* ── storage that never takes the shop down with it ──
     A private window, cleared site data, or a browser set to block
     storage all throw here. A wishlist is a convenience; losing it must
     not stop someone buying. */
  function readSaved() {
    try {
      var v = localStorage.getItem(K_SAVE);
      var a = v ? JSON.parse(v) : [];
      return Object.prototype.toString.call(a) === '[object Array]' ? a : [];
    } catch (e) { return []; }
  }
  function writeSaved(a) {
    try { localStorage.setItem(K_SAVE, JSON.stringify(a)); } catch (e) {}
  }

  /* ── the quiet confirmation ── */
  var toastTimer = null;
  function toast(msg) {
    var el = document.getElementById('toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('on'); }, 2600);
  }

  /* ── alternate templates as fragments ──
     ?view=popup renders templates/product.popup.liquid, which declares
     {% layout none %} — so the response is exactly the markup for the
     panel body and nothing else. No page chrome to strip, no duplicated
     content sitting hidden in the real product page's DOM, and every
     figure in it rendered by Liquid against the live product.

     This is why the popup can never be stale: it IS the product, fetched
     at the moment it is opened. */
  /* While a theme is being previewed rather than published, the page
     carries ?preview_theme_id=… and so must every request made from it.
     Without it a fetch is served by the LIVE theme, which has none of
     these alternate templates — so the response is an ordinary page,
     the fragment is not in it, and every panel and every category tab
     silently comes back empty while the same code works perfectly on a
     published theme. Carry the parameter through. */
  var PREVIEW = (function () {
    var m = /[?&](preview_theme_id=\d+)/.exec(window.location.search);
    return m ? m[1] : '';
  })();

  function fragment(url, view) {
    var q = url.indexOf('?') === -1 ? '?' : '&';
    var full = url + q + 'view=' + encodeURIComponent(view);
    if (PREVIEW && full.indexOf('preview_theme_id=') === -1) full += '&' + PREVIEW;
    return fetch(full, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error(view + ': ' + r.status);
      return r.text();
    });
  }

  function fill(panelId, html, titleText) {
    var panel = document.getElementById(panelId);
    if (!panel) return;
    var body = panel.querySelector('.p-body');
    if (body) { body.innerHTML = html; body.scrollTop = 0; }
    if (titleText != null) {
      var h = panel.querySelector('.p-title');
      if (h) h.textContent = titleText;
    }
    paintSaveButtons(panel);
  }

  function spinner(panelId) {
    var panel = document.getElementById(panelId);
    if (!panel) return;
    var body = panel.querySelector('.p-body');
    if (body) body.innerHTML = '<p class="p-wait">' + t('loading', 'One moment…') + '</p>';
  }

  function oops(panelId) {
    var panel = document.getElementById(panelId);
    if (!panel) return;
    var body = panel.querySelector('.p-body');
    if (body) {
      body.innerHTML = '<p class="p-wait">' +
        t('load_error', 'That did not load. Please try again.') + '</p>';
    }
  }

  /* ── the bag ── */
  function cartState() {
    return fetch(R.cart || '/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function paintCount(n) {
    document.querySelectorAll('[data-bag-count]').forEach(function (e) {
      e.textContent = n;
      e.hidden = !n;
    });
  }

  function refreshBag(open) {
    return Promise.all([cartState(), fragment(R.checkout || '/cart', 'panel')])
      .then(function (out) {
        paintCount(out[0].item_count);
        fill('p-bag', out[1]);
        if (open && window.__openPanel) window.__openPanel('p-bag');
      })
      .catch(function () { if (open) oops('p-bag'); });
  }

  function addToBag(variantId, qty, name) {
    if (busy) return;
    busy = true;
    fetch(R.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        busy = false;
        if (!res.ok) {
          /* Shopify answers an over-sell with a described error rather
             than a failure, and it is worth repeating verbatim: it is
             usually "only N left", which the customer needs to know. */
          toast(res.j && res.j.description ? res.j.description
                                           : t('add_error', 'That could not be added to your cart.'));
          return;
        }
        /* Short and always the same. The product's name was in here, which
           made the confirmation a different length for every product and
           two lines for the long ones — on a phone that is a banner, not a
           confirmation. The customer just pressed the button; they know
           what they added. */
        toast(t('added', 'Added to cart'));
        refreshBag(false);
      })
      .catch(function () {
        busy = false;
        toast(t('add_error', 'That could not be added to your cart.'));
      });
  }

  /* ── add, then open the bag ──
     "Buy now" used to leave for Shopify's checkout the moment the add
     succeeded. It now does what the rest of this theme does: adds the
     item and slides the bag open, with the real Checkout button sitting
     at the bottom of it. The customer sees what they are buying, the
     total, and any discount before they commit — and one more tap takes
     them to checkout from there.

     If the add fails, the bag does not open: an empty bag appearing after
     you pressed buy reads as the shop losing the order. */
  function addThenCheckout(variantId, qty, name, btn) {
    if (busy) return;
    busy = true;
    if (btn) { btn.classList.add('working'); btn.disabled = true; }
    function release() {
      busy = false;
      if (btn) { btn.classList.remove('working'); btn.disabled = false; }
    }
    fetch(R.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok) {
          release();
          toast(res.j && res.j.description ? res.j.description
                                           : t('add_error', 'That could not be added to your cart.'));
          return;
        }
        release();
        refreshBag(true);
      })
      .catch(function () {
        release();
        toast(t('add_error', 'That could not be added to your cart.'));
      });
  }

  function changeLine(line, qty) {
    if (busy) return;
    busy = true;
    fetch(R.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function () { busy = false; refreshBag(false); })
      .catch(function () { busy = false; });
  }

  /* ── the saved list ──
     Handles, not variant ids, because a handle survives a variant being
     re-created and it is what the card section is addressed by. */
  function isSaved(handle) { return readSaved().indexOf(handle) !== -1; }

  function toggleSave(handle, name) {
    var a = readSaved(), i = a.indexOf(handle);
    if (i === -1) { a.unshift(handle); toast((name || t('item', 'Item')) + ' ' + t('saved', 'saved')); }
    else { a.splice(i, 1); toast((name || t('item', 'Item')) + ' ' + t('unsaved', 'removed from saved')); }
    writeSaved(a);
    paintSaveButtons(document);
    var panel = document.getElementById('p-saved');
    if (panel && panel.classList.contains('open')) refreshSaved(false);
  }

  function paintSaveButtons(root) {
    var a = readSaved();
    (root || document).querySelectorAll('[data-save-toggle]').forEach(function (b) {
      var on = a.indexOf(b.getAttribute('data-save-toggle')) !== -1;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      var lab = b.querySelector('.sv-lab');
      if (lab) lab.textContent = on ? t('saved_label', 'Saved') : t('save_label', 'Save');
    });
    document.querySelectorAll('[data-save-count]').forEach(function (e) {
      e.textContent = a.length;
      e.hidden = a.length === 0;
    });
  }

  function refreshSaved(open) {
    var handles = readSaved();
    if (open && window.__openPanel) window.__openPanel('p-saved');
    if (!handles.length) {
      fill('p-saved', '<p class="bag-empty">' +
        t('saved_empty', 'Nothing saved yet. The heart on any bag keeps it here.') + '</p>');
      return Promise.resolve();
    }
    if (open) spinner('p-saved');
    /* Each card is rendered by the same Liquid snippet the grids use, so
       a saved item can never drift from the real one. A handle that no
       longer resolves is dropped rather than shown as a hole. */
    return Promise.all(handles.map(function (h) {
      return fragment('/products/' + h, 'card').catch(function () { return ''; });
    })).then(function (cards) {
      var live = cards.filter(Boolean);
      if (live.length !== handles.length) {
        writeSaved(handles.filter(function (h, i) { return cards[i]; }));
      }
      fill('p-saved', live.length
        ? '<div class="pgrid pgrid-s">' + live.join('') + '</div>'
        : '<p class="bag-empty">' + t('saved_empty', 'Nothing saved yet.') + '</p>');
      paintSaveButtons(document);
    });
  }

  /* ── the product popup ──
     Rendered from the product's own page, so it is the product page:
     same photographs, same description, same price, same stock. */
  function openProduct(url, name) {
    if (window.__openPanel) window.__openPanel('p-detail');
    spinner('p-detail');
    var panel = document.getElementById('p-detail');
    if (panel && name) {
      var h = panel.querySelector('.p-title');
      if (h) h.textContent = name;
    }
    fragment(url, 'popup')
      .then(function (html) {
        var d = document.createElement('div');
        d.innerHTML = html;
        var title = d.querySelector('[data-popup-title]');
        if (title) title.remove();
        fill('p-detail', d.innerHTML, title ? title.textContent.trim() : null);
        /* So a visitor can bookmark or share what they are looking at,
           without ever having navigated. Back closes the panel. */
        try { history.replaceState(history.state, '', url); } catch (e) {}
      })
      .catch(function () { oops('p-detail'); });
  }

  /* ── panels that fetch their own contents ──
     A collection panel carries data-src and is empty until it is first
     opened. That keeps two hundred product cards out of the homepage's
     initial HTML — which is a second of load on a phone — while leaving
     the row that opens it a real, crawlable link to the real collection.

     Fetched once, then kept: reopening a panel is instant. */
  function fillFromSource(panel) {
    if (!panel || panel.getAttribute('data-loaded') === '1') return;
    var src = panel.getAttribute('data-src');
    if (!src) return;
    panel.setAttribute('data-loaded', '1');
    spinner(panel.id);
    fragment(src, 'panel')
      .then(function (html) {
        fill(panel.id, html);
      })
      .catch(function () {
        panel.removeAttribute('data-loaded');
        oops(panel.id);
      });
  }

  /* The panel machinery in bustah.js owns opening; this only has to
     notice that a panel with a source has just been opened. Capture
     phase, so the fetch starts in the same frame as the animation. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-panel]');
    if (!trigger) return;
    var panel = document.getElementById(trigger.getAttribute('data-panel'));
    if (panel && panel.hasAttribute('data-src')) fillFromSource(panel);
  }, true);

  /* ── category tabs, filtering the grid in place ──
     The Shop section's tabs are the collection list. Pressing one swaps
     the grid for that collection's products without leaving the page,
     opening a panel, or losing the visitor's place.

     Every tab is a real <a href> to the real collection page, so a
     modified click, a crawler and a broken script all still get somewhere
     — only a plain left click becomes a filter.

     Grids are fetched once and cached for the rest of the visit; the
     opening grid is kept as it was rendered, so "All bags" costs nothing
     to go back to. */
  /* The grid's contents changed, so three things that were worked out at
     load are now wrong: which elements the reveal engine animates, where
     every section sits, and how tall the page is (which is what the film's
     scroll positions are measured against).

     The first of those is the one that bites. Cards injected into a
     staggered container are left at the opacity: 0 the stylesheet gives
     them, because the engine's list of elements was built before they
     existed — so the products are there and invisible, and the section
     reads as empty. */
  function regrid() {
    try {
      if (window.__rescan) window.__rescan();
      else if (window.__remeasure) window.__remeasure();
      window.dispatchEvent(new Event('resize'));
    } catch (err) {}
  }

  var TAB_CACHE = {};
  /* Captured once, at load, from the grid as Liquid rendered it — not
     lazily on the first tab press. Pressing Show more before touching a
     tab used to make the expanded grid become the thing "All bags"
     restored, so there was no way back to the opening view. */
  var TAB_HOME = null;
  var TAB_HOME_MORE = '';
  /* How many cards the section was built to hold. On the deck the Shop
     section is one screen with no scroll of its own, so this is also the
     most it can show — anything past it is handed to the panel, which
     scrolls. Without this a tab that returns a full collection page, or
     a press of Show more, pours forty cards into a box the height of one
     screen: the fit routine shrinks the whole section toward its floor
     and then clips what still will not go. */
  var HOME_COUNT = 0;
  (function () {
    var host = document.querySelector('[data-grid-host]');
    if (!host) return;
    TAB_HOME = host.innerHTML;
    HOME_COUNT = host.querySelectorAll('.pcard').length;
    var wrap = document.querySelector('[data-more-wrap]');
    var btn = wrap && wrap.querySelector('[data-shopmore]');
    TAB_HOME_MORE = (btn && wrap && !wrap.hidden) ? btn.getAttribute('data-shopmore') : '';
  })();

  /* Is the Shop section being squeezed onto one screen by the deck?

     It used to be, and everything below that asks this question was
     written for that: the grid was trimmed back to one screen's worth,
     and "Show more" handed the rest to a panel because a section that
     cannot scroll has nowhere to put more rows.

     The section now carries data-nofit — it keeps its natural height and
     scrolls like any other page — so the answer is no, and Show more
     does the obvious thing: it puts more products in the grid, in place.
     The question is still asked rather than deleted, so a section put
     back on the fit still behaves safely. */
  function onDeck() {
    var shop = document.querySelector('#shop');
    if (shop && shop.hasAttribute('data-nofit')) return false;
    return !!(window.__deckOn && window.__deckOn());
  }

  /* Trim the grid back to what one screen holds. Returns whether
     anything was taken off, so the caller knows there is more to offer. */
  function trimGrid(host) {
    if (!onDeck() || !HOME_COUNT) return false;
    var cards = host.querySelectorAll('.pcard');
    if (cards.length <= HOME_COUNT) return false;
    for (var i = cards.length - 1; i >= HOME_COUNT; i--) {
      if (cards[i].parentNode) cards[i].parentNode.removeChild(cards[i]);
    }
    return true;
  }

  /* Open the shop panel on one collection. The panel keeps whatever it
     fetched the first time, so pointing it somewhere new means clearing
     that cache as well as the body. */
  function openCollectionPanel(url, title) {
    var panel = document.getElementById('p-all');
    if (!panel || !url || !window.__openPanel) return false;
    if (panel.getAttribute('data-src') !== url) {
      panel.setAttribute('data-src', url);
      panel.removeAttribute('data-loaded');
      var body = panel.querySelector('.p-body');
      if (body) body.innerHTML = '';
    }
    var h = panel.querySelector('.p-title');
    if (h && title) h.textContent = title;
    fillFromSource(panel);
    window.__openPanel('p-all');
    return true;
  }

  function tabCap(host, title, note) {
    var cap = document.querySelector('[data-grid-cap]');
    if (!cap) return;
    var b = cap.querySelector('b'), s = cap.querySelector('span');
    if (b) b.textContent = title || '';
    if (s) { s.textContent = note || ''; s.hidden = !note; }
  }

  function showTab(btn) {
    var host = document.querySelector('[data-grid-host]');
    if (!host) return;
    var key = btn.getAttribute('data-tab');
    var title = btn.getAttribute('data-title') || '';
    var note = btn.getAttribute('data-note') || '';

    document.querySelectorAll('[data-tabs] .tab').forEach(function (a) {
      var on = a === btn;
      a.classList.toggle('on', on);
      a.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    function paint(html, moreUrl, mode) {
      host.innerHTML = html;
      host.classList.remove('loading');
      paintSaveButtons(host);
      tabCap(host, title, note);
      if (onDeck()) {
        /* One screen's worth here; the rest of this collection is one
           press away, in the panel. Show more points at the collection
           itself rather than at its next page, because the panel loads
           the whole thing and pages on from there. */
        var trimmed = trimGrid(host);
        var whole = (key === '__all') ? TAB_HOME_MORE : key;
        setMore((trimmed || moreUrl) ? whole : '', 'panel');
      } else {
        setMore(moreUrl, mode);
      }
      regrid();
    }

    if (key === '__all') { paint(TAB_HOME, TAB_HOME_MORE, 'replace'); return; }
    if (TAB_CACHE[key]) { paint(TAB_CACHE[key].html, TAB_CACHE[key].more, 'append'); return; }

    host.classList.add('loading');

    /* Two attempts, then give up honestly.

       First the alternate template, which returns just the grid. If that
       comes back as something we do not recognise — the usual cause is a
       request being served by a different theme than the one being
       looked at — try the ordinary collection page and take the grid out
       of it, because that page is marked up by this theme too. Only if
       both fail does the tab do what its href always said it would and
       go to the collection page.

       A tab that silently does nothing is the one outcome worth any
       amount of code to avoid. */
    function usable(html) {
      var d = document.createElement('div');
      d.innerHTML = html;
      return { grid: d.querySelector('[data-grid]'), empty: d.querySelector('.bag-empty'), doc: d };
    }

    fragment(key, 'panel')
      .then(function (html) {
        var r = usable(html);
        if (r.grid || r.empty) return r;
        return fetch(key, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
          .then(function (res) { return res.text(); })
          .then(function (page) { return usable(page); });
      })
      .then(function (r) {
        var d = r.doc, grid = r.grid, empty = r.empty;
        if (!grid && !empty) { window.location.href = btn.getAttribute('href') || key; return; }

        var out = grid && grid.children.length
          ? grid.innerHTML
          : '<p class="lede">' + t('collection_empty', 'Nothing in here yet.') + '</p>';
        var nextBtn = d.querySelector('[data-more]');
        var next = nextBtn ? nextBtn.getAttribute('data-more') : '';
        TAB_CACHE[key] = { html: out, more: next };
        paint(out, next, 'append');
      })
      .catch(function () {
        host.classList.remove('loading');
        toast(t('load_error', 'That did not load. Please try again.'));
      });
  }

  /* ── show more, under the grid ──
     Two modes, because the opening grid and a fetched one are different
     shapes. The opening grid is a handful of products rendered straight
     into the page, so the first press REPLACES it with the collection's
     full first page. Every press after that APPENDS the next page. */
  function setMore(url, mode) {
    var wrap = document.querySelector('[data-more-wrap]');
    if (!wrap) return;
    var btn = wrap.querySelector('[data-shopmore]');
    if (!btn) return;
    if (!url) { wrap.hidden = true; return; }
    wrap.hidden = false;
    btn.disabled = false;
    btn.textContent = t('show_more', 'Show more');
    btn.setAttribute('data-shopmore', url);
    btn.setAttribute('data-shopmore-mode', mode || 'append');
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-shopmore]');
    if (!btn) return;
    e.preventDefault();
    var host = document.querySelector('[data-grid-host]');
    if (!host) return;
    var url = btn.getAttribute('data-shopmore');
    var mode = btn.getAttribute('data-shopmore-mode') || 'append';

    /* On the deck the Shop section is one screen and does not scroll, so
       appending rows into it is the one thing Show more must not do —
       the section shrinks toward its floor and then clips. The panel is
       the surface built for a long list, so that is where more goes. It
       keeps the caption's own wording as its title, so the panel that
       opens is plainly the same thing the grid was showing. */
    if (onDeck()) {
      var cap = document.querySelector('[data-grid-cap] b');
      if (openCollectionPanel(url, cap ? cap.textContent.trim() : '')) return;
    }

    btn.disabled = true;
    btn.textContent = t('loading', 'One moment…');
    fragment(url, 'panel')
      .then(function (html) {
        var d = document.createElement('div');
        d.innerHTML = html;
        var grid = d.querySelector('[data-grid]');
        if (!grid) { setMore('', ''); return; }

        /* "replace" is really "append the part that is not already on
           screen". The opening grid is the first N products of this same
           collection in this same order, so page one's first N cards are
           the ones already showing — take everything after them.

           Doing it as a straight replace looked right on a big collection
           and shrank the grid on a small one, where a page holds fewer
           products than the opening view shows. Pressing "Show more" and
           getting less is the kind of thing nobody reports and everybody
           notices. */
        if (mode === 'replace') {
          var have = host.querySelectorAll('.pcard').length;
          var incoming = grid.querySelectorAll('.pcard');
          for (var i = have; i < incoming.length; i++) host.appendChild(incoming[i]);
        } else {
          host.insertAdjacentHTML('beforeend', grid.innerHTML);
        }
        paintSaveButtons(host);
        var nextBtn = d.querySelector('[data-more]');
        setMore(nextBtn ? nextBtn.getAttribute('data-more') : '', 'append');
        regrid();
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = t('show_more', 'Show more');
        toast(t('load_error', 'That did not load. Please try again.'));
      });
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tab]');
    if (!btn) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    showTab(btn);
  });

  /* More, inside a panel: fetch the next page and append it. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-more]');
    if (!btn) return;
    e.preventDefault();
    var url = btn.getAttribute('data-more');
    btn.disabled = true;
    btn.textContent = t('loading', 'One moment…');
    fragment(url, 'panel')
      .then(function (html) {
        var d = document.createElement('div');
        d.innerHTML = html;
        var incoming = d.querySelector('[data-grid]');
        var grid = btn.closest('.p-body').querySelector('[data-grid]');
        if (incoming && grid) grid.insertAdjacentHTML('beforeend', incoming.innerHTML);
        var nextBtn = d.querySelector('[data-more]');
        if (nextBtn) {
          btn.setAttribute('data-more', nextBtn.getAttribute('data-more'));
          btn.disabled = false;
          btn.textContent = t('load_more', 'More');
        } else {
          btn.closest('.p-more').remove();
        }
        paintSaveButtons(document);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = t('load_more', 'More');
      });
  });

  /* ── one delegated listener ── */
  document.addEventListener('click', function (e) {
    var t2 = e.target.closest(
      '[data-open-product],[data-add],[data-checkout],[data-line],[data-save-toggle],[data-open-saved],[data-open-bag]'
    );
    if (!t2) return;

    if (t2.hasAttribute('data-open-product')) {
      /* Let a modified click do what the visitor asked: a new tab is a
         real page, which is the whole point of these being real links. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      openProduct(t2.getAttribute('data-open-product') || t2.getAttribute('href'),
                  t2.getAttribute('data-name'));
      return;
    }
    if (t2.hasAttribute('data-add')) {
      e.preventDefault();
      addToBag(t2.getAttribute('data-add'), parseInt(t2.getAttribute('data-qty') || '1', 10),
               t2.getAttribute('data-name'));
      return;
    }
    if (t2.hasAttribute('data-line')) {
      e.preventDefault();
      changeLine(parseInt(t2.getAttribute('data-line'), 10),
                 parseInt(t2.getAttribute('data-to'), 10));
      return;
    }
    if (t2.hasAttribute('data-save-toggle')) {
      e.preventDefault();
      toggleSave(t2.getAttribute('data-save-toggle'), t2.getAttribute('data-name'));
      return;
    }
    if (t2.hasAttribute('data-open-saved')) { e.preventDefault(); refreshSaved(true); return; }
    if (t2.hasAttribute('data-checkout')) {
      e.preventDefault();
      addThenCheckout(t2.getAttribute('data-checkout'), 1,
                      t2.getAttribute('data-name'), t2);
      return;
    }
    if (t2.hasAttribute('data-open-bag')) { e.preventDefault(); refreshBag(true); return; }
  });

  /* Variant selection inside the popup, where a product has options. */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-variant-select]');
    if (!sel) return;
    var wrap = sel.closest('[data-variant-scope]');
    if (!wrap) return;
    var id = sel.value;
    var btn = wrap.querySelector('[data-add]');
    var avail = sel.options[sel.selectedIndex].getAttribute('data-available') === 'true';
    if (btn) {
      btn.setAttribute('data-add', id);
      btn.disabled = !avail;
      btn.textContent = avail ? t('add_to_cart', 'Add to cart') : t('sold_out', 'Sold out');
    }
    /* The checkout button carries the same id and has to move with it.
       Miss this and a customer picks brown, presses Checkout, and buys
       black — a mistake they only discover after paying. */
    var go = wrap.querySelector('[data-checkout]');
    if (go) {
      go.setAttribute('data-checkout', id);
      go.disabled = !avail;
    }
    /* data-price is the money filter's output as plain text, so this is
       textContent rather than innerHTML — the figure is Liquid's, and it
       is never treated as markup. */
    var priceHost = wrap.querySelector('[data-variant-price]');
    var price = sel.options[sel.selectedIndex].getAttribute('data-price');
    if (priceHost && price) priceHost.textContent = price;
    showVariantShot(wrap, sel.options[sel.selectedIndex]);
  });

  /* ── the gallery ──
     Thumbnails swap the large photograph, and so does choosing a variant.

     Each thumbnail carries its own full-size source now. The old version
     read a single space-joined list off the wrapper and indexed into it,
     which meant the picture only ever changed if that string had survived
     escaping, capture whitespace and the split intact — one stray space in
     a URL and every thumbnail silently did nothing. A source on the button
     it belongs to cannot get out of step. */
  function showShot(gal, btn) {
    if (!gal || !btn) return;
    var img = gal.querySelector('[data-pdshot]') || gal.querySelector('.pdshot img');
    if (!img) return;
    var src = btn.getAttribute('data-shot');
    if (!src) {
      /* Older markup: fall back to the joined list on the wrapper. */
      var srcs = (gal.getAttribute('data-shots') || '').split(/\s+/);
      var i = parseInt(btn.getAttribute('data-gal'), 10);
      src = srcs[i];
    }
    if (!src) return;
    /* srcset would otherwise keep winning over the src we just set. */
    img.removeAttribute('srcset');
    img.setAttribute('src', src);
    img.src = src;
    var mid = btn.getAttribute('data-mid');
    if (mid) img.setAttribute('data-mid', mid);
    var alt = btn.getAttribute('data-alt');
    if (alt) img.setAttribute('alt', alt);
    gal.querySelectorAll('[data-gal]').forEach(function (x) {
      x.classList.toggle('on', x === btn);
    });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-gal]');
    if (!b) return;
    e.preventDefault();
    showShot(b.closest('.pdgal'), b);
  });

  /* Choosing a variant moves the photograph to that variant's image — the
     thumbnail Shopify has assigned to it if there is one, and nothing at
     all if the merchant has not assigned one, which is better than showing
     an unrelated shot. */
  function showVariantShot(scope, opt) {
    if (!scope || !opt) return;
    var gal = scope.querySelector('.pdgal');
    if (!gal) return;
    var mid = opt.getAttribute('data-mid');
    if (mid) {
      var thumb = gal.querySelector('[data-gal][data-mid="' + mid + '"]');
      if (thumb) { showShot(gal, thumb); return; }
    }
    var src = opt.getAttribute('data-img');
    if (!src) return;
    var img = gal.querySelector('[data-pdshot]') || gal.querySelector('.pdshot img');
    if (!img) return;
    img.removeAttribute('srcset');
    img.src = src;
    gal.querySelectorAll('[data-gal]').forEach(function (x) { x.classList.remove('on'); });
  }

  /* Opening a product popup rewrites the URL to that product, so it can be
     bookmarked or shared without ever having navigated. Closing puts the
     page's own address back — driven by the panel module's close event, so
     it covers the X, the scrim and Escape rather than the two of them I
     happened to think of. */
  var HOME = window.location.pathname + window.location.search;
  document.addEventListener('bustah:panelclose', function () {
    try { history.replaceState(history.state, '', HOME); } catch (err) {}
  });

  /* ── applying a discount code from the cart ──

     POST /cart/update.js with {discount: "CODE"} is Shopify's documented
     way to do this, and it answers with the updated cart — so whether it
     worked is a fact in the response rather than a guess.

     The first version of this used /discount/CODE, the redirect route.
     That route stores the code for checkout but does not reliably show
     up in the cart, so a perfectly good code came back looking rejected.
     Wrong mechanism, and the UI then lied about the result.

     Success is not "is there a discount on this cart" either: this store
     runs an automatic Buy-2 discount, so there is often one already. It
     is "is there a discount whose title is the code they typed", which
     is what tells a code apart from an automatic. */
  function codeOnCart(cart, code) {
    var want = String(code || '').toLowerCase();
    var apps = (cart.cart_level_discount_applications || []).slice();
    (cart.items || []).forEach(function (i) {
      (i.line_level_discount_allocations || []).forEach(function (a) {
        if (a && a.discount_application) apps.push(a.discount_application);
      });
    });
    return apps.some(function (d) {
      if (!d) return false;
      if (d.type === 'automatic') return false;
      return String(d.title || '').toLowerCase() === want;
    });
  }

  function setDiscount(code) {
    return fetch(R.cartUpdate || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code })
    }).then(function (r) { return r.json(); });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('input[name="discount"]');
    var msg = form.querySelector('[data-discount-msg]');
    var btn = form.querySelector('button[type="submit"]');
    var code = (input && input.value || '').trim();
    if (!code) { if (input) input.focus(); return; }

    function say(text, ok) {
      if (!msg) return;
      msg.hidden = false;
      msg.textContent = text;
      msg.classList.toggle('bad', !ok);
      msg.classList.toggle('good', !!ok);
    }
    if (btn) { btn.disabled = true; btn.textContent = t('loading', 'One moment…'); }

    setDiscount(code)
      .then(function (cart) {
        if (btn) { btn.disabled = false; btn.textContent = t('discount_apply', 'Apply'); }
        if (codeOnCart(cart, code)) {
          toast(t('discount_ok', 'Code applied'));
          refreshBag(false);
        } else {
          say(t('discount_bad', 'That code was not accepted.'), false);
        }
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = t('discount_apply', 'Apply'); }
        say(t('discount_bad', 'That code was not accepted.'), false);
      });
  });

  /* Taking a code back off, for someone who typed the wrong one or found
     a better one. One request, the same endpoint, an empty string. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    setDiscount('')
      .then(function () { refreshBag(false); })
      .catch(function () { btn.disabled = false; });
  });

  /* ── copy a discount code ──
     Reading a code off a screen and retyping it at checkout is where a
     discount quietly stops being used: one wrong character and the
     customer decides the code was never real. One tap removes the
     typing. The clipboard API wants a secure context and a user gesture
     — this is inside a click, but it can still be refused, so there is
     an execCommand fallback and, failing both, the code is selected for
     the customer to copy by hand. It is plain text on the page either
     way, so nothing here is load-bearing. */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (res, rej) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
      document.body.removeChild(ta);
      ok ? res() : rej(new Error('copy refused'));
    });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-copy]');
    if (!b) return;
    var code = b.getAttribute('data-copy');
    if (!code) return;
    copyText(code).then(function () {
      /* Confirm on the control itself as well as in the toast: the bag
         version of this button can sit inside a scrolled panel where a
         toast at the edge of the screen is easy to miss. */
      var lab = b.querySelector('.oc-copy') || b.querySelector('i');
      if (lab && !b.getAttribute('data-was')) {
        b.setAttribute('data-was', lab.textContent);
        lab.textContent = t('copied', 'Copied');
        b.classList.add('copied');
        setTimeout(function () {
          lab.textContent = b.getAttribute('data-was');
          b.removeAttribute('data-was');
          b.classList.remove('copied');
        }, 1800);
      }
      toast(code + ' — ' + t('code_copied', 'code copied'));
    }).catch(function () {
      var n = b.querySelector('b') || b;
      try {
        var r = document.createRange();
        r.selectNodeContents(n);
        var s = window.getSelection();
        s.removeAllRanges();
        s.addRange(r);
      } catch (err) {}
      toast(t('copy_manual', 'Select the code and copy it') + ': ' + code);
    });
  });

  /* ── first paint ── */
  paintSaveButtons(document);
  cartState().then(function (c) { paintCount(c.item_count); }).catch(function () {});

  window.__bustahShop = {
    add: addToBag,
    openProduct: openProduct,
    refreshBag: refreshBag,
    refreshSaved: refreshSaved,
    isSaved: isSaved
  };
})();
